<template>
  <div class="misc-fee-ledger">
    <el-form :inline="true" :model="conditions" size="small" class="query-form">
      <el-form-item :label="$t('miscFeeLedger.feeType')">
        <el-select v-model="conditions.feeTypeCd" clearable :placeholder="$t('miscFeeLedger.all')">
          <el-option v-for="item in feeTypes" :key="item.value" :label="$t(item.label)" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.flowType')">
        <el-select v-model="conditions.flowType" clearable :placeholder="$t('miscFeeLedger.all')">
          <el-option value="1001" :label="$t('miscFeeLedger.income')" />
          <el-option value="2002" :label="$t('miscFeeLedger.refund')" />
          <el-option value="3003" :label="$t('miscFeeLedger.depositTransfer')" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.payerObjName')">
        <el-input v-model="conditions.payerObjName" clearable :placeholder="$t('miscFeeLedger.payerObjPlaceholder')" />
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.ownerName')">
        <el-input v-model="conditions.ownerName" clearable :placeholder="$t('miscFeeLedger.ownerPlaceholder')" />
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.operatorName')">
        <el-input v-model="conditions.operatorName" clearable :placeholder="$t('miscFeeLedger.operatorPlaceholder')" />
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.createUserName')">
        <el-input v-model="conditions.createUserName" clearable :placeholder="$t('miscFeeLedger.createUserPlaceholder')" />
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.orderId')">
        <el-input v-model="conditions.detailId" clearable :placeholder="$t('miscFeeLedger.orderIdPlaceholder')" />
      </el-form-item>
      <el-form-item :label="$t('miscFeeLedger.eventTime')">
        <el-date-picker
          v-model="dateRange"
          type="datetimerange"
          value-format="yyyy-MM-dd HH:mm:ss"
          :range-separator="$t('miscFeeLedger.to')"
          :start-placeholder="$t('miscFeeLedger.startTime')"
          :end-placeholder="$t('miscFeeLedger.endTime')"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="queryLedger">
          {{ $t('miscFeeLedger.query') }}
        </el-button>
        <el-button icon="el-icon-refresh" @click="resetQuery">
          {{ $t('miscFeeLedger.reset') }}
        </el-button>
        <el-button type="success" icon="el-icon-download" @click="exportLedger">
          {{ $t('common.export') }}
        </el-button>
      </el-form-item>
    </el-form>

    <div class="summary-strip">
      <div class="summary-item income-summary">
        <span>{{ $t('miscFeeLedger.incomeAmount') }}</span>
        <strong>{{ formatMoney(summary.incomeAmount) }}</strong>
      </div>
      <div class="summary-item refund-summary">
        <span>{{ $t('miscFeeLedger.refundAmount') }}</span>
        <strong>{{ formatMoney(summary.refundAmount) }}</strong>
      </div>
      <div class="summary-item deposit-transfer-summary">
        <span>{{ $t('miscFeeLedger.depositTransferAmount') }}</span>
        <strong>{{ formatMoney(summary.depositTransferAmount) }}</strong>
      </div>
      <div class="summary-item net-summary">
        <span>{{ $t('miscFeeLedger.netAmount') }}</span>
        <strong>{{ formatMoney(summary.netAmount) }}</strong>
      </div>
      <div class="summary-item count-summary">
        <span>{{ $t('miscFeeLedger.flowCount') }}</span>
        <strong>{{ summary.count || 0 }}</strong>
        <small>{{ $t('miscFeeLedger.flowCountDetail', {
          income: summary.incomeCount || 0,
          refund: summary.refundCount || 0,
          depositTransfer: summary.depositTransferCount || 0
        }) }}</small>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="ledger-tabs">
      <el-tab-pane :label="$t('miscFeeLedger.detailLedger')" name="detail">
        <el-table v-loading="loading" :data="ledgerList" border stripe>
          <el-table-column prop="eventTime" :label="$t('miscFeeLedger.eventTime')" width="165" />
          <el-table-column :label="$t('miscFeeLedger.flowType')" width="100">
            <template slot-scope="scope">
              <el-tag :type="flowTagType(scope.row.flowType)" size="small">{{ scope.row.flowTypeName }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="feeTypeCdName" :label="$t('miscFeeLedger.feeType')" min-width="105" />
          <el-table-column prop="feeName" :label="$t('miscFeeLedger.feeName')" min-width="130" show-overflow-tooltip />
          <el-table-column prop="payerObjName" :label="$t('miscFeeLedger.payerObjName')" min-width="125" show-overflow-tooltip />
          <el-table-column prop="ownerName" :label="$t('miscFeeLedger.ownerName')" min-width="100" />
          <el-table-column :label="$t('miscFeeLedger.amount')" width="115" align="right">
            <template slot-scope="scope">
              <span :class="amountClass(scope.row.amount)">{{ formatSignedMoney(scope.row.amount) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="primeRateName" :label="$t('miscFeeLedger.payMethod')" width="100" />
          <el-table-column prop="operatorName" :label="$t('miscFeeLedger.operatorName')" width="105" />
          <el-table-column prop="createUserName" :label="$t('miscFeeLedger.createUserName')" width="105" />
          <el-table-column prop="auditPersonName" :label="$t('miscFeeLedger.auditPersonName')" width="105">
            <template slot-scope="scope">{{ scope.row.auditPersonName || '-' }}</template>
          </el-table-column>
          <el-table-column prop="sourceOrderId" :label="$t('miscFeeLedger.sourceOrderId')" min-width="175" show-overflow-tooltip />
          <el-table-column prop="remark" :label="$t('miscFeeLedger.remark')" min-width="160" show-overflow-tooltip />
        </el-table>
        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :current-page="page.current"
            :page-size="page.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="page.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane :label="$t('miscFeeLedger.operatorStatistics')" name="operator">
        <el-table v-loading="loading" :data="operatorStatistics" border stripe>
          <el-table-column prop="operatorName" :label="$t('miscFeeLedger.operatorName')" min-width="150" />
          <el-table-column prop="flowCount" :label="$t('miscFeeLedger.flowCount')" min-width="100" align="right" />
          <el-table-column prop="incomeCount" :label="$t('miscFeeLedger.incomeCount')" min-width="100" align="right" />
          <el-table-column prop="refundCount" :label="$t('miscFeeLedger.refundCount')" min-width="100" align="right" />
          <el-table-column prop="depositTransferCount" :label="$t('miscFeeLedger.depositTransferCount')" min-width="110" align="right" />
          <el-table-column :label="$t('miscFeeLedger.incomeAmount')" min-width="130" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.incomeAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('miscFeeLedger.refundAmount')" min-width="130" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.refundAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('miscFeeLedger.depositTransferAmount')" min-width="140" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.depositTransferAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('miscFeeLedger.netAmount')" min-width="130" align="right">
            <template slot-scope="scope">
              <span :class="amountClass(scope.row.netAmount)">{{ formatMoney(scope.row.netAmount) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane :label="$t('miscFeeLedger.billingOperatorStatistics')" name="billingOperator">
        <el-table v-loading="loading" :data="billingStatistics" border stripe>
          <el-table-column prop="createUserName" :label="$t('miscFeeLedger.createUserName')" min-width="150" />
          <el-table-column prop="flowCount" :label="$t('miscFeeLedger.flowCount')" min-width="100" align="right" />
          <el-table-column prop="incomeCount" :label="$t('miscFeeLedger.incomeCount')" min-width="100" align="right" />
          <el-table-column prop="refundCount" :label="$t('miscFeeLedger.refundCount')" min-width="100" align="right" />
          <el-table-column prop="depositTransferCount" :label="$t('miscFeeLedger.depositTransferCount')" min-width="110" align="right" />
          <el-table-column :label="$t('miscFeeLedger.incomeAmount')" min-width="130" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.incomeAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('miscFeeLedger.refundAmount')" min-width="130" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.refundAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('miscFeeLedger.depositTransferAmount')" min-width="140" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.depositTransferAmount) }}</template>
          </el-table-column>
          <el-table-column :label="$t('miscFeeLedger.netAmount')" min-width="130" align="right">
            <template slot-scope="scope">
              <span :class="amountClass(scope.row.netAmount)">{{ formatMoney(scope.row.netAmount) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { queryMiscFeeLedger, exportData } from '@/api/report/miscFeeLedgerApi'

export default {
  name: 'MiscFeeLedgerList',
  data() {
    return {
      activeTab: 'detail',
      loading: false,
      dateRange: [],
      conditions: {
        feeTypeCd: '', flowType: '', payerObjName: '', ownerName: '', operatorName: '', createUserName: '', detailId: ''
      },
      feeTypes: [
        { value: '888800010020', label: 'miscFeeLedger.advertisementFee' },
        { value: '888800010021', label: 'miscFeeLedger.decorationDeposit' },
        { value: '888800010022', label: 'miscFeeLedger.temporaryServiceFee' },
        { value: '888800010023', label: 'miscFeeLedger.venueMiscellaneousFee' }
      ],
      ledgerList: [],
      operatorStatistics: [],
      billingStatistics: [],
      summary: {},
      page: { current: 1, size: 10, total: 0 }
    }
  },
  mounted() {
    this.listLedger()
  },
  methods: {
    async listLedger() {
      this.loading = true
      try {
        const params = {
          ...this.conditions,
          page: this.page.current,
          row: this.page.size,
          startTime: this.dateRange && this.dateRange[0] ? this.dateRange[0] : '',
          endTime: this.dateRange && this.dateRange[1] ? this.dateRange[1] : ''
        }
        const result = await queryMiscFeeLedger(params)
        this.ledgerList = result.data || []
        this.operatorStatistics = result.rep || []
        this.summary = result.sumTotal || {}
        this.billingStatistics = this.summary.billingStatistics || []
        this.page.total = result.total || 0
      } finally {
        this.loading = false
      }
    },
    queryLedger() {
      this.page.current = 1
      this.listLedger()
    },
    resetQuery() {
      this.conditions = {
        feeTypeCd: '', flowType: '', payerObjName: '', ownerName: '', operatorName: '', createUserName: '', detailId: ''
      }
      this.dateRange = []
      this.page.current = 1
      this.listLedger()
    },
    async exportLedger() {
      this.loading = true
      try {
        const params = {
          ...this.conditions,
          startTime: this.dateRange && this.dateRange[0] ? this.dateRange[0] : '',
          endTime: this.dateRange && this.dateRange[1] ? this.dateRange[1] : '',
          pagePath: 'reportMiscFeeLedger'
        }
        const res = await exportData(params)
        if (res.code === 0) {
          this.$message.success(res.msg)
          this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
        } else {
          this.$message.error(res.msg || this.$t('miscFeeLedger.exportFailed'))
        }
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error(this.$t('miscFeeLedger.exportFailed'))
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.listLedger()
    },
    handleCurrentChange(current) {
      this.page.current = current
      this.listLedger()
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatSignedMoney(value) {
      const amount = Number(value || 0)
      return `${amount > 0 ? '+' : ''}${amount.toFixed(2)}`
    },
    amountClass(value) {
      return Number(value || 0) < 0 ? 'amount-negative' : 'amount-positive'
    },
    flowTagType(flowType) {
      if (flowType === '1001') return 'success'
      if (flowType === '3003') return 'warning'
      return 'danger'
    }
  }
}
</script>

<style lang="scss" scoped>
.misc-fee-ledger {
  min-height: calc(100vh - 84px);
  padding: 20px;
  background: #f5f7fa;
}

.query-form,
.ledger-tabs {
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #e4e7ed;
}

.query-form {
  padding-bottom: 0;
}

.query-form ::v-deep .el-input,
.query-form ::v-deep .el-select {
  width: 190px;
}

.query-form ::v-deep .el-date-editor {
  width: 380px;
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(170px, 1fr));
  margin: 14px 0;
  background: #fff;
  border: 1px solid #e4e7ed;
}

.summary-item {
  min-height: 92px;
  padding: 17px 20px;
  border-top: 3px solid #6b7280;
  border-right: 1px solid #ebeef5;
}

.summary-item:last-child {
  border-right: 0;
}

.summary-item span,
.summary-item small {
  display: block;
  color: #606266;
  font-size: 13px;
}

.summary-item strong {
  display: block;
  margin: 7px 0 3px;
  color: #303133;
  font-size: 24px;
  line-height: 1.2;
}

.income-summary { border-top-color: #2f855a; }
.refund-summary { border-top-color: #c53030; }
.deposit-transfer-summary { border-top-color: #d69e2e; }
.net-summary { border-top-color: #2b6cb0; }

.amount-positive {
  color: #237a50;
  font-weight: 600;
}

.amount-negative {
  color: #b83232;
  font-weight: 600;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  padding-top: 18px;
}

@media (max-width: 900px) {
  .misc-fee-ledger { padding: 12px; }
  .summary-strip { grid-template-columns: repeat(2, minmax(150px, 1fr)); }
  .summary-item:nth-child(even) { border-right: 0; }
  .query-form ::v-deep .el-date-editor { width: 100%; max-width: 380px; }
}

@media (max-width: 520px) {
  .summary-strip { grid-template-columns: 1fr; }
  .summary-item { border-right: 0; border-bottom: 1px solid #ebeef5; }
}
</style>
